// Apolo IDE — CIMonitorMenu (Pedro 2026-06-25, sprint cursor-strip-3).
//
// Drop-up "Monitoramento de CI" (estilo Cursor) com dados REAIS do GitHub
// Actions pra (owner, repo, ref=branch). Mostra:
//   · Rollup (Aprovado / Falhou / Em andamento / Sem checks)
//   · Counts por bucket (Cursor: "Aprovado 1 · Falhou 3")
//   · Lista expansível de check-runs com link pro detalhe no GitHub
//   · 2 toggles LS-backed: Corrigir CI automaticamente · Mesclar automaticamente
//   · Configurações de arquivamento automático (link p/ docs)
//
// useGitChecks faz polling a cada 30s — sem mockup. Quando GitHub não está
// conectado, oferece CTA pra abrir o Connections.

import { useEffect, useRef, useState } from 'react';
import {
  Activity,
  Check,
  Loader2,
  Settings,
  X,
  ExternalLink,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { useGitChecks } from '../../hooks/useGitChecks.js';

const LS_AUTO_FIX = 'apolo:ci:auto-fix';
const LS_AUTO_MERGE = 'apolo:ci:auto-merge';

function lsBool(key, fallback = false) {
  if (typeof window === 'undefined') return fallback;
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return fallback;
  }
}

function lsSetBool(key, value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value ? '1' : '0');
    window.dispatchEvent(new CustomEvent('apolo:ci:settings:change', { detail: { key, value } }));
  } catch {
    /* ignore */
  }
}

// loop-eng-harvest E2 — bridge the toggles to ~/.apolo/pr-babysitter.json for the driver.
// window.apolo is the Electron preload bridge (absent in the web build); cast once for tsc.
function persistBabysitter(config) {
  const w = /** @type {any} */ (window);
  try { w?.apolo?.prBabysitter?.set?.(config); } catch { /* web build / not in Electron */ }
}

const BUCKET_META = {
  passed: { label: 'Aprovado', color: 'var(--apolo-success, #16a34a)', icon: Check },
  failed: { label: 'Falhou', color: 'var(--apolo-error, #dc2626)', icon: X },
  pending: { label: 'Em andamento', color: 'var(--apolo-warn, #f59e0b)', icon: Loader2 },
  skipped: { label: 'Pulado', color: 'var(--apolo-textTertiary, #6b7280)', icon: AlertTriangle },
  neutral: { label: 'Neutro', color: 'var(--apolo-textTertiary, #6b7280)', icon: AlertTriangle },
};

export default function CIMonitorMenu({
  open,
  onClose,
  owner,
  repo,
  // (Pedro 2026-06-25, round 9) — era `ref`, palavra RESERVADA do React: ela
  // é interceptada e NUNCA chega como prop → o branch do CI vinha undefined.
  // Renomeado pra `gitRef` (corrige o bug E o lint react/no-string-refs).
  gitRef,
  anchorClassName = 'absolute bottom-full right-0 z-50 mb-2',
}) {
  const { data, loading, error, refresh } = useGitChecks({
    owner,
    repo,
    ref: gitRef,
    enabled: open,
    pollMs: 30_000,
  });
  const [autoFix, setAutoFix] = useState(() => lsBool(LS_AUTO_FIX));
  const [autoMerge, setAutoMerge] = useState(() => lsBool(LS_AUTO_MERGE));
  const menuRef = useRef(null);

  // Cross-tab + cross-component sync.
  useEffect(() => {
    function onChange(e) {
      const { key, value } = e?.detail || {};
      if (key === LS_AUTO_FIX) setAutoFix(!!value);
      if (key === LS_AUTO_MERGE) setAutoMerge(!!value);
    }
    function onStorage(e) {
      if (e.key === LS_AUTO_FIX) setAutoFix(lsBool(LS_AUTO_FIX));
      if (e.key === LS_AUTO_MERGE) setAutoMerge(lsBool(LS_AUTO_MERGE));
    }
    window.addEventListener('apolo:ci:settings:change', onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('apolo:ci:settings:change', onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Click-outside / Esc.
  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose?.();
    }
    function onKey(e) {
      if (e.key === 'Escape') onClose?.();
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  const summary = data?.summary || { passed: 0, failed: 0, pending: 0, skipped: 0, neutral: 0, total: 0 };
  const buckets = ['passed', 'failed', 'pending', 'skipped'].filter((b) => summary[b] > 0);
  const notConnected = error === 'GitHub não conectado';

  return (
    <div
      ref={menuRef}
      role="dialog"
      aria-label="Monitoramento de CI"
      data-testid="ci-monitor-menu"
      className={
        anchorClassName +
        ' min-w-[280px] max-w-[360px] rounded-md border border-apolo-borderSubtle bg-apolo-surface1 p-1 shadow-xl backdrop-blur-md'
      }
    >
      <div className="flex items-center justify-between px-2 py-1.5">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-apolo-textPrimary">
          <Activity size={11} aria-hidden="true" />
          Monitoramento de CI
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={refresh}
            aria-label="Atualizar"
            title="Atualizar agora"
            disabled={loading}
            className="inline-flex h-4 w-4 items-center justify-center rounded text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary disabled:opacity-40"
          >
            {loading ? (
              <Loader2 size={11} aria-hidden="true" className="animate-spin" />
            ) : (
              <RefreshCw size={11} aria-hidden="true" />
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="inline-flex h-4 w-4 items-center justify-center rounded text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
          >
            <X size={11} aria-hidden="true" />
          </button>
        </div>
      </div>

      {notConnected && (
        <div className="px-2 py-2 text-[11px] text-apolo-textTertiary">
          GitHub não conectado.{' '}
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('apolo:openConnections'))}
            className="text-apolo-textSecondary underline hover:text-apolo-textPrimary"
          >
            Conectar
          </button>
          {' '}para ver CI real.
        </div>
      )}

      {!notConnected && error && (
        <p role="alert" className="px-2 py-1.5 text-[10.5px] text-apolo-error">{error}</p>
      )}

      {!notConnected && !error && (!data || summary.total === 0) && !loading && (
        <p className="px-2 py-1.5 text-[11px] text-apolo-textTertiary">
          Nenhum check configurado pra <span className="font-mono">{gitRef || '—'}</span>.
        </p>
      )}

      {!notConnected && summary.total > 0 && (
        <>
          <ul className="px-2 py-1">
            {buckets.map((b) => {
              const meta = BUCKET_META[b];
              const Icon = meta.icon;
              return (
                <li
                  key={b}
                  data-testid={`ci-bucket-${b}`}
                  className="flex items-center justify-between gap-2 py-0.5 text-[12px]"
                >
                  <span className="inline-flex items-center gap-1.5" style={{ color: meta.color }}>
                    <Icon size={11} aria-hidden="true" />
                    <span className="text-apolo-textPrimary">{meta.label}</span>
                  </span>
                  <span className="font-mono text-apolo-textSecondary">{summary[b]}</span>
                </li>
              );
            })}
          </ul>

          {Array.isArray(data?.items) && data.items.length > 0 && (
            <>
              <div className="my-1 h-px bg-apolo-borderSubtle" aria-hidden="true" />
              <ul className="flex max-h-[180px] flex-col overflow-y-auto px-1">
                {data.items.slice(0, 20).map((it) => {
                  const meta = BUCKET_META[it.bucket] || BUCKET_META.neutral;
                  const Icon = meta.icon;
                  const Outer = it.url ? 'a' : 'div';
                  const outerProps = it.url
                    ? { href: it.url, target: '_blank', rel: 'noreferrer' }
                    : {};
                  return (
                    <li key={it.id}>
                      <Outer
                        {...outerProps}
                        className="flex items-center gap-2 rounded-md px-1.5 py-1 text-[11px] text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
                      >
                        <Icon
                          size={10}
                          aria-hidden="true"
                          style={{ color: meta.color, flex: '0 0 auto' }}
                        />
                        <span className="truncate font-mono" title={it.name}>{it.name}</span>
                        {it.url && (
                          <ExternalLink
                            size={10}
                            aria-hidden="true"
                            className="ml-auto shrink-0 text-apolo-textTertiary"
                          />
                        )}
                      </Outer>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </>
      )}

      <div className="my-1 h-px bg-apolo-borderSubtle" aria-hidden="true" />
      <div className="flex flex-col gap-0.5 px-2 py-1">
        <label className="flex items-center gap-2 py-1 text-[11px] text-apolo-textSecondary">
          <input
            type="checkbox"
            checked={autoFix}
            onChange={(e) => {
              const v = e.target.checked;
              setAutoFix(v);
              lsSetBool(LS_AUTO_FIX, v);
              persistBabysitter({ autoFix: v, autoMerge }); // driver reads ~/.apolo/pr-babysitter.json
            }}
            data-testid="ci-auto-fix"
            className="h-3 w-3 rounded border-apolo-borderSubtle"
          />
          Corrigir CI automaticamente e responder comentários
        </label>
        <label className="flex items-center gap-2 py-1 text-[11px] text-apolo-textSecondary">
          <input
            type="checkbox"
            checked={autoMerge}
            onChange={(e) => {
              const v = e.target.checked;
              setAutoMerge(v);
              lsSetBool(LS_AUTO_MERGE, v);
              persistBabysitter({ autoFix, autoMerge: v });
            }}
            data-testid="ci-auto-merge"
            className="h-3 w-3 rounded border-apolo-borderSubtle"
          />
          Mesclar automaticamente quando pronto
        </label>
      </div>

      <div className="my-1 h-px bg-apolo-borderSubtle" aria-hidden="true" />
      <button
        type="button"
        onClick={() => window.dispatchEvent(new CustomEvent('apolo:openCISettings'))}
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[11px] text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
      >
        <Settings size={11} aria-hidden="true" />
        Configurações de arquivamento automático
      </button>
    </div>
  );
}
